import { cn } from "@/utils";
import type { ElementType, HTMLAttributes, ReactNode } from "react";

/**
 * Tipografia do Design System.
 *
 * Existe para que ninguém precise decidir tamanho/peso/cor no JSX: escolhe-se o
 * papel do texto na tela ("é um título de seção", "é um metadado") e o
 * componente resolve o resto. Títulos saem em Sora (display), corpo em IBM Plex
 * Sans — ver src/design-system/DESIGN.md.
 */

type HeadingLevel = 1 | 2 | 3 | 4;

const headingSize: Record<HeadingLevel, string> = {
  1: "text-ds-h1",
  2: "text-ds-h2",
  3: "text-ds-h3",
  4: "text-ds-h4",
};

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Nível semântico (h1–h4). Não pule níveis: leitores de tela navegam por eles. */
  level?: HeadingLevel;
  /** Usa o tamanho de outro nível sem mudar a semântica do HTML. */
  size?: HeadingLevel;
  children: ReactNode;
}

export const Heading = ({
  level = 2,
  size,
  className,
  children,
  ...props
}: HeadingProps) => {
  const Tag = `h${level}` as ElementType;
  const visual = size ?? level;
  // Merriweather (serifada da LP) só nos títulos grandes. Em 20–24px, numa tela
  // densa, ela fica pesada e atrapalha a leitura — aí vale a Sora, que é o que o
  // produto já faz hoje com os títulos de card e de seção.
  const family = visual <= 2 ? "font-ds-display" : "font-ds-sans font-semibold";
  return (
    <Tag
      className={cn(
        "text-ds-ink-900 text-balance",
        family,
        headingSize[visual],
        className
      )}
      {...props}
    >
      {children}
    </Tag>
  );
};

type TextTone =
  | "default"
  | "muted"
  | "subtle"
  | "brand"
  | "success"
  | "warning"
  | "danger";
type TextSize = "caption" | "small" | "body" | "lead";

const toneClass: Record<TextTone, string> = {
  // Os três níveis param em ink-600 de propósito: ink-500 (#808080) dá 3.8:1 e
  // reprova em AA para texto normal — é borda e ícone, não texto.
  default: "text-ds-ink-900",
  muted: "text-ds-ink-700",
  subtle: "text-ds-ink-600",
  brand: "text-ds-brand-700",
  success: "text-ds-success-700",
  warning: "text-ds-warning-700",
  danger: "text-ds-danger-700",
};

const sizeClass: Record<TextSize, string> = {
  caption: "text-ds-caption",
  small: "text-ds-small",
  body: "text-ds-body",
  lead: "text-ds-lead",
};

export interface TextProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType;
  size?: TextSize;
  tone?: TextTone;
  weight?: "regular" | "medium" | "semibold";
  /** Dígitos tabulares — use em qualquer coluna de dinheiro, data ou contagem. */
  numeric?: boolean;
  children: ReactNode;
}

export const Text = ({
  as: Tag = "p",
  size = "body",
  tone = "default",
  weight = "regular",
  numeric = false,
  className,
  children,
  ...props
}: TextProps) => (
  <Tag
    className={cn(
      "font-ds-sans",
      sizeClass[size],
      toneClass[tone],
      weight === "medium" && "font-medium",
      weight === "semibold" && "font-semibold",
      numeric && "ds-numeric",
      className
    )}
    {...props}
  >
    {children}
  </Tag>
);

type MetricSize = "default" | "compact";

const metricValueSize: Record<MetricSize, string> = {
  default: "text-ds-h2",
  compact: "text-ds-h3",
};

/**
 * Um número em destaque: dinheiro, contagem, percentual, tempo.
 *
 * O valor é um **átomo**: nenhuma parte dele quebra da outra. Medido no
 * navegador, "R$ 100.000,00" em 30px ocupa 208px e não cabe nos 192px úteis do
 * cartão de KPI do painel em 1280px — o "R$" ficava órfão numa linha e o número
 * sozinho na de baixo. Por isso os espaços do valor viram espaço inseparável
 * aqui dentro, e não na tela que formata o número.
 */
export const Metric = ({
  value,
  label,
  hint,
  size = "default",
  className,
}: {
  /**
   * Já formatado em pt-BR pela tela — "R$ 1.234,56", "4.812", "63,4%".
   * Se vier `string`, os espaços são convertidos em espaço inseparável.
   */
  value: ReactNode;
  label: string;
  hint?: ReactNode;
  /**
   * `default` (30px) é o valor solto ou em grade de até 4 colunas.
   * `compact` (24px) é para grade de 5+ colunas ou coluna estreita: a 24px,
   * "R$ 12.345.678,90" mede ~189px e cabe no cartão de 192px úteis em 1280px.
   */
  size?: MetricSize;
  className?: string;
}) => (
  // `min-w-0`: sem isso a coluna da grade adota a largura do número e o cartão
  // empurra a grade inteira para fora da tela.
  <div className={cn("flex min-w-0 flex-col gap-1", className)}>
    <Text as="span" size="small" tone="muted" weight="medium">
      {label}
    </Text>
    {/* Sem `ds-numeric`: aqui o valor é único, não há coluna para alinhar — então
        vale mais a serifada da marca que a monoespaçada.
        `break-words` é a última linha de defesa: com o valor inteiro colado, um
        número absurdo (bilhões) vazaria por cima do cartão vizinho; quebrado
        dentro do cartão ele continua legível, ainda que feio. */}
    <span
      className={cn(
        "font-ds-display text-ds-ink-900 font-bold break-words",
        metricValueSize[size]
      )}
    >
      {typeof value === "string" ? value.replace(/ /g, " ") : value}
    </span>
    {hint ? (
      <Text as="span" size="caption" tone="subtle">
        {hint}
      </Text>
    ) : null}
  </div>
);
